import { memo, useCallback, useMemo } from 'react';
import { useRuntimeBlockLevels } from '../../../context/BlockRuntimeStateProvider/context';
import { useNonReactiveComputeContext } from '../../../hooks/computeContext';
import type { RowScope } from '../../../utils/repeatables/attachRowScopeIdentities';
import { isRuntimeBlockId } from '../../../utils/runTimeBlocks';
import type { WithBlockComputationsProps, WithContextProps } from '../types';

// The fast/slow decision now lives in `useBlockRendererCore`. Code paths that reach
// this HOC are guaranteed to have graph deps or dynamic paths, so the previous
// `WithContextResolver` short-circuit is no longer needed.
export const withContext = (WrappedComponent: React.ComponentType<WithBlockComputationsProps>) => {
  function WithContextWrapper(props: WithContextProps) {
    const { block, blocksRuntimeState, slotContextSnapshots, blockStateId } = props;
    const originalBlockId = block.id;

    // Implicit "this instance" scoping for the active block's ancestor Repeatables.
    // `useRuntimeBlockLevels` returns the inner-first stack of {parentBlockId, instanceKey}
    // entries built up by every BlockRuntimeStateProvider on the path from this block back to
    // the page root. Outside any row the stack is empty.
    const levels = useRuntimeBlockLevels();
    const rowScopeStack = useMemo<readonly RowScope[] | undefined>(() => {
      if (!levels?.length) return undefined;
      return levels;
    }, [levels]);

    const { context } = useNonReactiveComputeContext({
      entityId: originalBlockId,
      // if blockStateId is a runtime block id, we need to pass it as a key otherwise, we send undefined because we just want to
      // subscribe to entityId
      key: isRuntimeBlockId(blockStateId) ? blockStateId : undefined,
      blocksRuntimeState,
      slotContextSnapshots,
      rowScopeStack,
    });

    const getGlobalStoreContext = useCallback(() => context, [context]);

    return (
      <WrappedComponent
        {...props}
        context={context}
        getGlobalStoreContext={getGlobalStoreContext}
      />
    );
  }

  return memo(WithContextWrapper);
};
